import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UseQueryResult } from "@tanstack/react-query";

/**
 * Validates id parameter and redirects to list page if invalid
 * @param idParam - The name of the id parameter in the route (e.g., 'companyId', 'talentId', 'id')
 * @param listRoute - The route to redirect to if validation fails
 * @param queryResult - The query result from useQuery to check if data exists
 */
export const useValidateId = <TData = unknown, TError = unknown>(idParam: string, listRoute: string, queryResult?: UseQueryResult<TData, TError>) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params[idParam];

  const isValidId = useMemo(() => {
    if (!id) {
      return false;
    }
    const idNumber = Number(id);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [id]);

  useEffect(() => {
    if (!isValidId) {
      navigate(listRoute, { replace: true });
      return;
    }

    if (queryResult) {
      const { isError, error, data, isSuccess, isLoading } = queryResult;

      if (isLoading) {
        return;
      }

      if (isSuccess && !data) {
        navigate(listRoute, { replace: true });
        return;
      }

      if (isError) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError?.response?.status === 404 || !data) {
          navigate(listRoute, { replace: true });
          return;
        }
      }
    }
  }, [id, isValidId, idParam, listRoute, navigate, queryResult]);
};
